import React, {useEffect, useContext, useCallback, ReactElement, useState} from "react";

import {FetchResult, MutationResult, QueryResult} from "@apollo/client";
import WaitSpinner from "./wait-spinner";
import FieldTable from "./field-table";
import {ToolbarContext} from "../contexts/toolbar-context";
import {DiaFormRelationships, useDiaForm} from "./dia-form";
import {useUserContext} from '../contexts/user-context';
import {FieldTableOptional} from './FieldTable/FieldTableOptional';
import {NonIdealState} from "./NonIdealState";
import {useSnackbar} from "notistack";
import {FieldTableColumnProps} from "./FieldTable/FieldTableTypes";
import GQLChanges from "../utils/graphql-changes";
import { graphqlFindResults } from "../graphql-helpers";
import {DiaPromptConfirmation} from "./DiaConfimation";
import {useBlocker} from "./usePrompt";


interface YupSchema {
    validate: (any, Object) => Promise<unknown>
}

export type DataResult<Type> = {
    data: Type[]
    [key:string]: any
}

//var debug = new Set();
type ListEditorArgs<Type> = {
    query: QueryResult<any>
    className: string,
    saveResult: MutationResult,
    // raw mutation
    mutation:(options:any)=>Promise<FetchResult>,
    // wrapped mutation
    save?:(variables:any)=>Promise<FetchResult>,

    title: string,
    columns: FieldTableColumnProps<Type>[],
    optionalColumns?: FieldTableColumnProps<Type>[],
    newRow: () => Type,
    baseUrl: string,
    newRowLabel: string,
    shownColumns?: string,
    validationSchema?: YupSchema,
    permission: string,
    relationships?: DiaFormRelationships,
    maxOptionalColumns?: number,
    prepareVariables?:(any)=>any,
    onBeforeSubmit?:(any)=>boolean,
    setShownColumns?:(columns:string[])=>null,
    setOptionalColumns?:(any)=>null,
    getRowProps?:(row:Type)=>any,
    getValues: (query: any) => Type[] // an object that may have Type buried somewhere inside it
}


export function ListEditor<Type>(props: ListEditorArgs<Type>): ReactElement {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    //const [isRedirectAllowed,setIsRedirectAllowed] = useState(false);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const {loading, error, refetch} = props.query;
    const user = useUserContext();
    const [isRedirectAllowed, setIsRedirectAllowed] = useState(false);
    const [redirectCallback,setRedirectCallback] = useState(null);
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    const [isOptionalListOpen,setIsOptionalListOpen] = useState<HTMLElement>(null);
    // ignore the save state and change URL regardless
    const discardChanges = useCallback(()=>{
        console.log('discard clicked',redirectCallback);
        setIsRedirectAllowed(true);
        redirectCallback.retry();
        //       setRedirectCallback(null);
    },[redirectCallback,setIsRedirectAllowed]);

    // go to the new url
    const returnToEdit = useCallback(()=> {
        console.log('discard - RTE');
        // clear the block and forget it existed
//        redirectCallback.retry();
        setRedirectCallback(null);
    },[redirectCallback]);

    // do save, but do not redirect
    //const rows = props.getValues(props.query);
    const doSubmit = useCallback(({values, initialValues,updateInitialValues,hasCompleted,hasFailed}) => {
        console.log('doSubmit',values,initialValues);
        if (props.onBeforeSubmit) {
            if (!props.onBeforeSubmit({values, initialValues})) {
                return;
            }
        }
        const changes = new GQLChanges(props.relationships??{});
        const variables = changes.compare(initialValues, values);

        console.log('[ListEditor] v0',variables);
        //let updateVariables = props.getValues(variables);
        let updateVariables = variables;
        console.log('[ListEditor]',updateVariables);
        if (props.prepareVariables) {
            updateVariables = props.prepareVariables(updateVariables);
        }
        console.log('[ListEditor]', 'do submit compare', initialValues, values,variables);
        enqueueSnackbar('Saving..', {key: 'SAVE_LIST'});
        let promise:undefined|Promise<any> = undefined;
        if(props.save) {
            promise = props.save(updateVariables);
        } else {
            promise = props.mutation({variables:{update:updateVariables}});
        }
        return promise.then((result)=>{
            hasCompleted();
            if(updateVariables.create?.length||updateVariables.delete?.length) {
                // graphql merges do not merge with local creation lines, and it's hard to resolve that
                // instead we need to wipe all of the data and re-install it
                // deletes are obviously weird
                console.log('updating to',result,graphqlFindResults(result));
                updateInitialValues(graphqlFindResults(result));
                refetch();
            }
            closeSnackbar('SAVE_LIST');
            enqueueSnackbar('Saved', {key: 'SAVE_LIST_END', variant: 'success'});
        }).catch(()=> {
            hasFailed();
            closeSnackbar('SAVE_LIST');
            enqueueSnackbar('Save failed', {key: 'SAVE_LIST_END', variant: 'error'});
        })
    }, []);
    const diaForm = useDiaForm({
        inputValues: props.getValues(props.query?.data),
        onSubmit: doSubmit,
        validationSchema: props.validationSchema,
        failure: false,
        blankValues: () => null,
        loaded: !props.query.loading,
        reinitialize: false
    });
    const doSave = useCallback(()=> {
        // save and hopefully redirect afterwards
        // TODO: redirect only on successful save
        console.log('discard - SAVE');
        diaForm.handleSubmit();
        setRedirectCallback(null);
    },[diaForm.handleSubmit,redirectCallback]);


    const {isDirty, handleSubmit, status} = diaForm;
    const toolbarContext = useContext(ToolbarContext);
    useEffect(() => {
        const buttons = [{
            type: 'button',
            icon: 'save',
            tooltip: 'Save',
            disabled: !isDirty,
            callback: handleSubmit,
            label: 'Save'
        }];
        if(props.optionalColumns) {
            buttons.unshift({
                type:"button",
                icon:"columns",
                disabled:false,
                tooltip:"Change columns",
                callback:(evt)=>{
                    setIsOptionalListOpen(evt.currentTarget);
                },
                label:'Columns'
            });
        }


        toolbarContext.setSecondary(
            {
                title: props.title,
                secondary: buttons
            });
    }, [isDirty,!!props.optionalColumns]);
    const setOptionalColumns = useCallback((v) => {
        if (diaForm.isDirty) {
            enqueueSnackbar('Please save before changing columns', {key: 'COL_CHANGE', variant: 'error'});
            return;
        }
        if(props.setShownColumns) {
            props.setShownColumns(v);
        }
    }, [diaForm.isDirty, props.setOptionalColumns]);
    const blocker = ( tx ) => {
       setRedirectCallback(tx);
        //setIsRedirectAllowed(false);
    };
    useEffect(()=> {
        if(isRedirectAllowed && redirectCallback) {
            redirectCallback.retry();
            setRedirectCallback(null);
            setIsRedirectAllowed(false);
        }
    },[isRedirectAllowed,redirectCallback]);
    useBlocker( blocker, diaForm.isDirty);


    console.log('[ListEditor]', {data:props.query.data,loading: loading, error: error, form: diaForm, ...props});
//    console.log('[ListEditor]','PROPS',debug);
    if (error) {
        return <NonIdealState title="An error has occurred" severity="error" message={error.message}/>;
    }

    if (status == 'loading') {
        return <WaitSpinner variant="panel"/>;
    }
    const confirmation = <DiaPromptConfirmation isActive={!!redirectCallback} discard={discardChanges} returnToEdit={returnToEdit} save={doSave} saveFailed={
        false
    }/>
    const when = (tx)=>{
        return diaForm.isDirty;
    }
    return (
        <div className={'list-editor is-scrollable ' + (props.className ? ' ' + props.className : '')}>
            {props.saveResult.loading &&
                <WaitSpinner variant="overlay"/>
            }
            {confirmation}
            {/*
            <ReactRouterPrompt
                when={(crntLocation, nextLocation) => {
                    console.log('[ListEditor]','redirect check prompt', crntLocation, nextLocation, isRedirectAllowed);
                    console.log('[ListEditor]','redirect check prompt result', nextLocation.pathname.startsWith(crntLocation.pathname), crntLocation);
                
                    if (nextLocation.pathname.startsWith(crntLocation.pathname)) {
                        return false;
                    }
                    if (diaForm.isDirty && !isRedirectAllowed) {
                        return true;
                    } else {
                        setIsRedirectAllowed(true);
                        return false;
                    }
                }}>
                {({ isActive, onCancel, onConfirm }) => {
                    if (!diaForm.isDirty) {
                        onConfirm();
                    }
                    return <DiaPromptConfirmation onCancel={onCancel} onConfirm={onConfirm} isActive={isActive} doSave={diaForm.handleSubmit}
                        saveFailed={diaForm.errors} />;
                }
                }</ReactRouterPrompt>*/
            }
            {!props.maxOptionalColumns &&
                <FieldTable name=""
                            columns={props.columns}
                            newRow={user.hasPermission(props.permission + '.create') ? props.newRow : null}
                            getRowProps={props.getRowProps}
                            form={diaForm}
                            readOnly={!user.hasPermission(props.permission + '.edit')}
                            hasDelete={user.hasPermission(props.permission + '.delete')}
                            newRowLabel={props.newRowLabel}
                />
            }
            {props.maxOptionalColumns&& props.maxOptionalColumns > 0 &&
                <FieldTableOptional name=""
                                    columns={props.columns}
                                    newRow={user.hasPermission(props.permission + '.create') ? props.newRow : null}
                                    getRowProps={props.getRowProps}
                                    form={diaForm}
                                    setShownColumns={setOptionalColumns}
                                    maxOptionalColumns={props.maxOptionalColumns}
                                    shownColumns={props.shownColumns}
                                    isOptionalListOpen={isOptionalListOpen}
                                    setIsOptionalListOpen={setIsOptionalListOpen}
                                    optionalColumns={props.optionalColumns}
                                    readOnly={!user.hasPermission(props.permission + '.edit')}
                                    hasDelete={user.hasPermission(props.permission + '.delete')}
                                    newRowLabel={props.newRowLabel}
                />
            }
        </div>
    );
};
